import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  deriveFeedNameFromUrl,
  getSubstackSectionOverride,
  normalizeFeedUrl,
} from '@/lib/tracking';
import { invalidateFeed } from '@/lib/feed-sync';
import { CUSTOM_RSS_CREATION_ENABLED } from '@/lib/features';
import {
  clearSupabaseResourceMissing,
  isMissingSupabaseResource,
  isSupabaseResourceMarkedMissing,
  markSupabaseResourceMissing,
} from '@/lib/supabase-errors';

interface Feed {
  id: string;
  name: string;
  url: string;
  category: string;
  section: string;
  type: string;
  origin: string;
  provider: string;
  priority: number;
  description: string | null;
  enabled: boolean;
  paywalled?: boolean;
  last_error?: string | null;
  quality_score?: number | null;
  recent_item_count?: number | null;
}

interface Subscription {
  feed_id: string;
}

interface Profile {
  plan: 'free' | 'pro';
  custom_source_limit: number;
  custom_feed_count: number;
}

interface CustomSourceFeed {
  id: string;
  name: string;
  url: string;
  type: string;
  enabled: boolean;
  last_error?: string | null;
}

interface CustomSource {
  id: string;
  feed_id: string | null;
  original_url: string;
  normalized_url: string;
  display_name: string | null;
  section: string;
  status: 'validating' | 'active' | 'error' | 'disabled';
  last_error: string | null;
  created_at: string;
  feed: CustomSourceFeed | null;
}

interface FeedQualitySnapshot {
  feed_id: string;
  quality_score: number;
  item_count: number;
}

const SECTION_COLORS: Record<string, string> = {
  news: '#58a6ff',
  research: '#a371f7',
  newsletters: '#3fb950',
  podcasts: '#d29922',
};

const SECTION_DESCRIPTIONS: Record<string, string> = {
  news: 'Market-moving reporting, company moves, and regulatory developments.',
  research: 'Primary research, model labs, and deep technical infrastructure coverage.',
  newsletters: 'Opinionated analysis and investor-grade synthesis from trusted writers.',
  podcasts: 'Long-form conversations worth keeping in your reading flow.',
};

const CUSTOM_PLACEMENT_SECTIONS = ['news', 'research', 'newsletters'] as const;

function formatSectionLabel(section: string) {
  return section.charAt(0).toUpperCase() + section.slice(1);
}

const CATEGORY_LABELS: Record<string, string> = {
  ai: 'AI',
  ml: 'ML',
  vc: 'VC',
  eu: 'EU',
  saas: 'SaaS',
  wsj: 'WSJ',
  sec: 'SEC',
  cnbc: 'CNBC',
};

function formatCategoryLabel(category: string) {
  return category
    .split('/')
    .map((segment) =>
      segment
        .split(/[-_\s]+/)
        .filter(Boolean)
        .map(
          (word) =>
            CATEGORY_LABELS[word.toLowerCase()] ??
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ')
    )
    .join(' / ');
}

export function SourcesPage() {
  const { supabaseClient } = useAuth();
  const [userId, setUserId] = useState<string | null>(null);
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [customSources, setCustomSources] = useState<CustomSource[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [subscriptions, setSubscriptions] = useState<Set<string>>(new Set());
  const [activeSection, setActiveSection] = useState<string>('all');
  const [customUrl, setCustomUrl] = useState('');
  const [customName, setCustomName] = useState('');
  const [customPlacement, setCustomPlacement] =
    useState<(typeof CUSTOM_PLACEMENT_SECTIONS)[number]>('news');
  const [creatingCustomFeed, setCreatingCustomFeed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customRssNotice, setCustomRssNotice] = useState<string | null>(null);
  const [customSourcesUnavailable, setCustomSourcesUnavailable] = useState(false);
  const [profileUnavailable, setProfileUnavailable] = useState(false);
  const [feedQualitySnapshotsAvailable, setFeedQualitySnapshotsAvailable] = useState(
    isSupabaseResourceMarkedMissing('feed_quality_snapshots', null) === false
  );
  const [feedQualityOrderingLive, setFeedQualityOrderingLive] = useState(true);

  // Resolve Supabase user ID from the active session
  useEffect(() => {
    if (!supabaseClient) {
      setUserId(null);
      return;
    }

    // Get current session
    void supabaseClient.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });

    // Subscribe to auth changes (catches async Edge Function session)
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => subscription.unsubscribe();
  }, [supabaseClient]);

  useEffect(() => {
    setFeeds([]);
    setCustomSources([]);
    setSubscriptions(new Set());
    setProfile(null);
    setLoading(true);
    setError(null);
    setCustomRssNotice(null);
    setCustomSourcesUnavailable(false);
    setProfileUnavailable(false);
    setFeedQualitySnapshotsAvailable(
      isSupabaseResourceMarkedMissing('feed_quality_snapshots', userId) === false
    );
    setFeedQualityOrderingLive(true);
  }, [userId]);

  const loadData = useCallback(async () => {
    if (!userId || !supabaseClient) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setCustomRssNotice(null);

    const notices: string[] = [];

    const [catalogRes, subsRes] = await Promise.all([
      supabaseClient
        .from('feeds')
        .select(
          'id, name, url, category, section, type, origin, provider, priority, description, enabled, paywalled, last_error'
        )
        .eq('origin', 'catalog')
        .eq('enabled', true)
        .order('section')
        .order('priority', { ascending: false }),
      supabaseClient.from('subscriptions').select('feed_id').eq('user_id', userId),
    ]);

    if (catalogRes.error || subsRes.error) {
      setError('Could not load your sources right now.');
      setLoading(false);
      return;
    }

    const catalogFeedIds = (catalogRes.data ?? []).map((feed) => feed.id);
    let qualityByFeedId = new Map<string, FeedQualitySnapshot>();
    const feedQualityResourceMissing = isSupabaseResourceMarkedMissing(
      'feed_quality_snapshots',
      userId
    );

    if (feedQualityResourceMissing) {
      setFeedQualitySnapshotsAvailable(false);
      setFeedQualityOrderingLive(false);
    }

    if (catalogFeedIds.length > 0 && feedQualitySnapshotsAvailable && !feedQualityResourceMissing) {
      const { data: qualityRows, error: qualityError } = await supabaseClient
        .from('feed_quality_snapshots')
        .select('feed_id, quality_score, item_count')
        .in('feed_id', catalogFeedIds);

      if (qualityError) {
        if (isMissingSupabaseResource(qualityError)) {
          markSupabaseResourceMissing('feed_quality_snapshots', userId);
          setFeedQualitySnapshotsAvailable(false);
        }
        setFeedQualityOrderingLive(false);
      } else {
        clearSupabaseResourceMissing('feed_quality_snapshots', userId);
        setFeedQualitySnapshotsAvailable(true);
        setFeedQualityOrderingLive(true);
        qualityByFeedId = new Map(
          ((qualityRows ?? []) as FeedQualitySnapshot[]).map((row) => [row.feed_id, row])
        );
      }
    }

    const { data: profileData, error: profileError } = await supabaseClient
      .from('profiles')
      .select('plan, custom_source_limit, custom_feed_count')
      .eq('id', userId)
      .single();

    if (profileError) {
      notices.push(
        'Pro custom RSS controls are temporarily unavailable. The curated starter pack still works normally.'
      );
      setProfileUnavailable(true);
    } else if (profileData) {
      setProfile(profileData as Profile);
      setProfileUnavailable(false);
    }

    const nextSubscriptions = new Set((subsRes.data ?? []).map((row: Subscription) => row.feed_id));
    let nextCustomSources: CustomSource[] = [];

    const { data: customSourceRows, error: customSourcesError } = await supabaseClient
      .from('custom_sources')
      .select(
        `
        id,
        feed_id,
        original_url,
        normalized_url,
        display_name,
        section,
        status,
        last_error,
        created_at,
        feed:feeds (
          id,
          name,
          url,
          type,
          enabled,
          last_error
        )
      `
      )
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (customSourcesError) {
      notices.push(
        'Custom source details are temporarily unavailable right now. Existing sources remain intact, and the last loaded state will be shown when possible.'
      );
      setCustomSourcesUnavailable(true);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      nextCustomSources = (customSourceRows ?? []).map((row: any) => ({
        ...row,
        feed: Array.isArray(row.feed) ? (row.feed[0] ?? null) : (row.feed ?? null),
      })) as CustomSource[];
      setCustomSourcesUnavailable(false);
    }

    const sortedFeeds = (catalogRes.data ?? [])
      .map((feed) => {
        const snapshot = qualityByFeedId.get(feed.id);
        return {
          ...feed,
          quality_score: snapshot?.quality_score ?? null,
          recent_item_count: snapshot?.item_count ?? null,
        };
      })
      .sort((left, right) => {
        if (left.section !== right.section) return left.section.localeCompare(right.section);
        const qualityDelta = (right.quality_score ?? -1) - (left.quality_score ?? -1);
        if (Math.abs(qualityDelta) > 0.001) return qualityDelta;
        if (right.priority !== left.priority) return right.priority - left.priority;
        return left.name.localeCompare(right.name);
      });

    setFeeds(sortedFeeds);
    setCustomSources((prev) => (customSourcesError ? prev : nextCustomSources));
    setSubscriptions(nextSubscriptions);
    setCustomRssNotice(notices.length > 0 ? notices.join(' ') : null);
    setLoading(false);
  }, [userId, supabaseClient, feedQualitySnapshotsAvailable]);

  useEffect(() => {
    if (!userId || !supabaseClient) return;
    void loadData();
  }, [userId, supabaseClient, loadData]);

  async function toggleCatalogSubscription(feedId: string) {
    if (!userId || !supabaseClient) return;
    setError(null);

    if (subscriptions.has(feedId)) {
      const { error: deleteError } = await supabaseClient
        .from('subscriptions')
        .delete()
        .match({ user_id: userId, feed_id: feedId });

      if (deleteError) {
        setError('Could not update that source. Please try again.');
        return;
      }

      setSubscriptions((prev) => {
        const next = new Set(prev);
        next.delete(feedId);
        return next;
      });
      invalidateFeed('source-removed');
      return;
    }

    const { error: insertError } = await supabaseClient
      .from('subscriptions')
      .insert({ user_id: userId, feed_id: feedId });

    if (insertError) {
      setError('Could not update that source. Please try again.');
      return;
    }

    setSubscriptions((prev) => new Set(prev).add(feedId));
    invalidateFeed('source-added');
  }

  async function subscribeToStarterPack(section: string) {
    if (!userId || !supabaseClient) return;
    const sectionFeeds = feeds.filter(
      (feed) => feed.section === section && !subscriptions.has(feed.id)
    );
    if (sectionFeeds.length === 0) return;
    setError(null);

    const rows = sectionFeeds.map((feed) => ({ user_id: userId, feed_id: feed.id }));
    const { error: insertError } = await supabaseClient.from('subscriptions').insert(rows);
    if (insertError) {
      setError('Could not add all sources in that section.');
      return;
    }

    setSubscriptions((prev) => {
      const next = new Set(prev);
      for (const feed of sectionFeeds) next.add(feed.id);
      return next;
    });
    invalidateFeed(`source-section-added:${section}`);
  }

  async function unsubscribeFromSection(section: string) {
    if (!userId || !supabaseClient) return;
    const sectionFeeds = feeds.filter(
      (feed) => feed.section === section && subscriptions.has(feed.id)
    );
    if (sectionFeeds.length === 0) return;
    setError(null);

    const deleteResults = await Promise.all(
      sectionFeeds.map((feed) =>
        supabaseClient.from('subscriptions').delete().match({ user_id: userId, feed_id: feed.id })
      )
    );

    if (deleteResults.some((result) => result.error)) {
      setError('Could not remove all sources in that section.');
      return;
    }

    setSubscriptions((prev) => {
      const next = new Set(prev);
      for (const feed of sectionFeeds) next.delete(feed.id);
      return next;
    });
    invalidateFeed(`source-section-removed:${section}`);
  }

  async function addCustomFeed() {
    if (!userId || !supabaseClient || !profile) return;

    if (!CUSTOM_RSS_CREATION_ENABLED) {
      setError('Custom RSS creation is not enabled yet for this rollout.');
      return;
    }

    const normalizedUrl = normalizeFeedUrl(customUrl);
    if (!normalizedUrl) {
      setError('Enter a valid RSS or Atom URL.');
      return;
    }

    const platformSectionOverride = getSubstackSectionOverride(normalizedUrl);
    const requestedSection = platformSectionOverride ?? customPlacement;

    if (profile.plan !== 'pro') {
      setError('Custom RSS is available on Pro accounts only.');
      return;
    }

    setCreatingCustomFeed(true);
    setError(null);

    const { data, error: invokeError } = await supabaseClient.functions.invoke(
      'manage-custom-feed',
      {
        body: {
          action: 'create',
          url: normalizedUrl,
          section: requestedSection,
          name: customName.trim() || deriveFeedNameFromUrl(normalizedUrl),
        },
      }
    );

    if (invokeError || data?.error) {
      setError(data?.error ?? invokeError?.message ?? 'Could not add that custom source.');
      setCreatingCustomFeed(false);
      return;
    }

    const fallbackDisplayName = customName.trim() || deriveFeedNameFromUrl(normalizedUrl);
    const displayName = data?.custom_source?.display_name ?? fallbackDisplayName;

    const createdSource: CustomSource | null = data?.custom_source
      ? {
          id: data.custom_source.id,
          feed_id: data.custom_source.feed_id ?? data.feed?.id ?? null,
          original_url: customUrl.trim(),
          normalized_url: data.custom_source.normalized_url ?? normalizedUrl,
          display_name: displayName,
          section: data.custom_source.section ?? requestedSection,
          status: data.custom_source.status ?? 'active',
          last_error: data.custom_source.last_error ?? null,
          created_at: new Date().toISOString(),
          feed: data.feed
            ? {
                id: data.feed.id,
                name: data.feed.name,
                url: data.feed.url ?? normalizedUrl,
                type: data.feed.type ?? 'rss',
                enabled: true,
                last_error: data.custom_source.last_error ?? null,
              }
            : null,
        }
      : null;

    if (createdSource) {
      setCustomSources((prev) => [
        createdSource,
        ...prev.filter((source) => source.id !== createdSource.id),
      ]);
      setCustomSourcesUnavailable(false);
    }

    if (data?.created) {
      setProfile((prev) =>
        prev ? { ...prev, custom_feed_count: prev.custom_feed_count + 1 } : prev
      );
    }

    setCustomUrl('');
    setCustomName('');
    setCustomPlacement('news');
    setCreatingCustomFeed(false);
    invalidateFeed('custom-source-added');
    void loadData();
  }

  async function removeCustomFeed(customSourceId: string) {
    if (!userId || !supabaseClient) return;
    setError(null);

    const { data, error: invokeError } = await supabaseClient.functions.invoke(
      'manage-custom-feed',
      {
        body: {
          action: 'delete',
          customSourceId,
        },
      }
    );

    if (invokeError || data?.error) {
      setError(data?.error ?? invokeError?.message ?? 'Could not remove that custom source.');
      return;
    }

    setCustomSources((prev) => prev.filter((source) => source.id !== customSourceId));
    setCustomSourcesUnavailable(false);
    setProfile((prev) =>
      prev ? { ...prev, custom_feed_count: Math.max(prev.custom_feed_count - 1, 0) } : prev
    );

    invalidateFeed('custom-source-removed');
    void loadData();
  }

  const sections = ['news', 'research', 'newsletters', 'podcasts'];
  const visibleFeedIds = useMemo(() => new Set(feeds.map((feed) => feed.id)), [feeds]);
  const catalogSubscriptionCount = [...subscriptions].filter((id) => visibleFeedIds.has(id)).length;
  const customSourceCount = customSources.filter((source) => source.status !== 'disabled').length;
  const managedCustomSourceCount =
    profile?.custom_feed_count ?? (customSourcesUnavailable ? null : customSourceCount);
  const profileSyncing = profileUnavailable && !profile;
  const isPro = profile?.plan === 'pro';

  const filteredFeeds =
    activeSection === 'all'
      ? feeds
      : activeSection === 'subscribed'
        ? feeds.filter((feed) => subscriptions.has(feed.id))
        : feeds.filter((feed) => feed.section === activeSection);

  const feedsBySection: Record<string, Feed[]> = {};
  for (const section of sections) {
    const sectionFeeds = filteredFeeds.filter((feed) => feed.section === section);
    if (sectionFeeds.length > 0) {
      feedsBySection[section] = sectionFeeds;
    }
  }

  const showSectionHeaders = activeSection === 'all';
  const customLimit = isPro ? Math.max(profile.custom_source_limit ?? 0, 20) : 0;

  if (!supabaseClient) {
    return (
      <div className="page-shell">
        <div className="empty-state-panel">
          <p className="type-title" style={{ color: 'var(--text-primary)' }}>
            Sources Unavailable
          </p>
          <p className="type-body" style={{ color: 'var(--text-secondary)' }}>
            Source management requires additional setup. Please sign out and sign back in.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="page-shell-narrow py-12 text-center"
        style={{ color: 'var(--text-tertiary)' }}
      >
        Loading sources...
      </div>
    );
  }

  function renderFeedRow(feed: Feed) {
    const isSubscribed = subscriptions.has(feed.id);
    return (
      <div
        key={feed.id}
        onClick={() => void toggleCatalogSubscription(feed.id)}
        className="flex items-start gap-3 px-3 py-3 rounded-xl transition-notion"
        style={{
          background: isSubscribed ? 'var(--accent-secondary)' : 'rgba(255, 255, 255, 0.34)',
          border: isSubscribed ? '1px solid rgba(35, 131, 226, 0.18)' : '1px solid transparent',
          cursor: 'pointer',
        }}
      >
        <button
          onClick={(event) => {
            event.stopPropagation();
            void toggleCatalogSubscription(feed.id);
          }}
          className="shrink-0 type-caption font-medium rounded-full transition-notion"
          style={{
            minWidth: '72px',
            padding: '6px 12px',
            background: isSubscribed ? 'var(--accent-primary)' : 'rgba(255, 255, 255, 0.7)',
            color: isSubscribed ? '#fff' : 'var(--text-secondary)',
            border: isSubscribed
              ? '1px solid var(--accent-primary)'
              : '1px solid var(--border-primary)',
          }}
        >
          {isSubscribed ? 'Added' : 'Add'}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <div className="type-body font-medium" style={{ color: 'var(--text-primary)' }}>
              {feed.name}
            </div>
            <span className="pill-meta type-caption" style={{ color: 'var(--text-secondary)' }}>
              {formatCategoryLabel(feed.category)}
            </span>
            {feed.paywalled && (
              <span
                className="pill-meta type-caption"
                style={{ color: 'var(--warning)' }}
                title="Paid source"
              >
                Paid source
              </span>
            )}
          </div>
          {feed.description && (
            <div className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
              {feed.description}
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderSectionHeader(section: string) {
    const sectionFeeds = feeds.filter((feed) => feed.section === section);
    const subscribedCount = sectionFeeds.filter((feed) => subscriptions.has(feed.id)).length;
    const totalCount = sectionFeeds.length;
    const color = SECTION_COLORS[section] ?? 'var(--text-tertiary)';
    const canAdd = subscribedCount < totalCount;
    const canRemove = subscribedCount > 0;

    return (
      <div key={`header-${section}`} className="section-header">
        <div className="section-title-group">
          <div className="section-title-row">
            <span className="section-dot shrink-0" style={{ background: color }} />
            <span className="type-label font-semibold" style={{ color: 'var(--text-primary)' }}>
              {section.charAt(0).toUpperCase() + section.slice(1)}
            </span>
            <span className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
              {subscribedCount} of {totalCount} added
            </span>
          </div>
          <div className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
            {SECTION_DESCRIPTIONS[section]}
          </div>
        </div>
        <div className="section-actions">
          <button
            onClick={() => void subscribeToStarterPack(section)}
            className="action-pill type-caption font-medium"
            disabled={!canAdd}
            style={{
              color: canAdd ? color : 'var(--text-tertiary)',
              borderColor: canAdd ? `${color}33` : 'var(--border-primary)',
            }}
          >
            Add all
          </button>
          <button
            onClick={() => void unsubscribeFromSection(section)}
            className="action-pill type-caption font-medium"
            disabled={!canRemove}
            style={{ color: 'var(--text-tertiary)' }}
          >
            Remove all
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell-narrow">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Your reading list</div>
          <h1 className="page-title">Sources</h1>
          <p className="page-subtitle">
            {feedQualityOrderingLive
              ? 'Start with the curated starter pack. Pro custom RSS will let you add niche feeds once creation is enabled.'
              : feedQualitySnapshotsAvailable
                ? 'Start with the curated starter pack. If the list looks a little out of date for a moment, refresh and try again.'
                : 'Start with the curated starter pack. If the list looks a little out of date for a moment, refresh and try again.'}
          </p>
        </div>
        <div className="page-metrics">
          <span className="metric-chip type-caption">
            <strong>{catalogSubscriptionCount}</strong>
            curated
          </span>
          <span className="metric-chip type-caption">
            <strong>{managedCustomSourceCount ?? 'Unavailable'}</strong>
            custom RSS
          </span>
        </div>
      </div>

      {error && (
        <div
          className="mb-4 rounded-md px-3 py-2 type-caption"
          style={{
            background: 'var(--error-bg)',
            color: 'var(--error)',
            border: '1px solid rgba(224, 62, 62, 0.2)',
          }}
        >
          {error}
        </div>
      )}

      <div className="surface-panel mb-4">
        <div className="type-overline mb-3" style={{ color: 'var(--text-tertiary)' }}>
          Curated starter pack
        </div>
        <div className="filter-bar">
          {['all', 'subscribed', ...sections].map((section) => (
            <button
              key={section}
              onClick={() => setActiveSection(section)}
              className="filter-pill type-label"
              style={
                activeSection === section
                  ? {
                      background: 'var(--accent-primary)',
                      borderColor: 'var(--accent-primary)',
                      color: '#fff',
                    }
                  : undefined
              }
            >
              {section === 'all'
                ? 'All sources'
                : section === 'subscribed'
                  ? `Subscribed (${catalogSubscriptionCount})`
                  : formatSectionLabel(section)}
            </button>
          ))}
          <span
            className="type-caption ml-auto hidden md:inline"
            style={{ color: 'var(--text-tertiary)' }}
          >
            Curated list
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {showSectionHeaders ? (
          sections.map((section) => {
            const sectionFeeds = feedsBySection[section];
            if (!sectionFeeds || sectionFeeds.length === 0) return null;
            return (
              <div key={section} className="surface-panel section-panel">
                {renderSectionHeader(section)}
                <div className="flex flex-col gap-2">
                  {sectionFeeds.map((feed) => renderFeedRow(feed))}
                </div>
              </div>
            );
          })
        ) : filteredFeeds.length > 0 ? (
          <div className="surface-panel">
            <div className="flex flex-col gap-2">
              {filteredFeeds.map((feed) => renderFeedRow(feed))}
            </div>
          </div>
        ) : (
          <div className="empty-state-panel">
            <div className="page-eyebrow">Nothing here yet</div>
            <div className="page-title" style={{ fontSize: '30px', margin: 0 }}>
              No sources in this view
            </div>
            <p className="page-subtitle" style={{ textAlign: 'center', maxWidth: '30rem' }}>
              {activeSection === 'subscribed'
                ? 'You have not added any curated feeds yet. Start with a section above, then come back to refine the list.'
                : 'Try another section or clear the filter to browse the full starter pack.'}
            </p>
          </div>
        )}
      </div>

      {(customSources.length > 0 || customSourcesUnavailable) && (
        <div className="surface-panel mt-4 mb-4">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <div className="page-eyebrow">Managed custom sources</div>
              <div className="type-title-lg mt-1" style={{ color: 'var(--text-primary)' }}>
                Your managed custom sources
              </div>
              <p
                className="type-caption mt-1"
                style={{ color: 'var(--text-tertiary)', maxWidth: '36rem' }}
              >
                These are your private custom sources.
              </p>
            </div>
          </div>

          {customSourcesUnavailable && customSources.length === 0 ? (
            <div
              className="rounded-xl px-4 py-4 type-body"
              style={{
                background: 'rgba(255, 255, 255, 0.46)',
                border: '1px dashed var(--border-secondary)',
                color: 'var(--text-secondary)',
              }}
            >
              Managed custom sources are temporarily unavailable. Existing sources remain intact,
              but the list is not loading right now.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {customSources.map((source) => (
                <div
                  key={source.id}
                  className="flex items-start gap-3 px-3 py-3 rounded-xl"
                  style={{
                    background: 'rgba(255, 255, 255, 0.4)',
                    border: '1px solid var(--border-primary)',
                  }}
                >
                  <button
                    onClick={() => void removeCustomFeed(source.id)}
                    className="shrink-0 type-caption font-medium rounded-full transition-notion"
                    style={{
                      minWidth: '88px',
                      padding: '6px 12px',
                      background: 'rgba(255, 255, 255, 0.72)',
                      color: 'var(--text-secondary)',
                      border: '1px solid var(--border-primary)',
                    }}
                  >
                    Remove
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <div
                        className="type-body font-medium"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {source.display_name ??
                          source.feed?.name ??
                          deriveFeedNameFromUrl(source.normalized_url)}
                      </div>
                      <span
                        className="pill-meta type-caption"
                        style={{ color: 'var(--accent-primary)' }}
                      >
                        Custom RSS
                      </span>
                      <span
                        className="pill-meta type-caption"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        {formatSectionLabel(source.section)}
                      </span>
                      <span
                        className="pill-meta type-caption"
                        style={{
                          color:
                            source.status === 'error' ? 'var(--error)' : 'var(--text-secondary)',
                        }}
                      >
                        {source.status === 'validating'
                          ? 'Validating'
                          : source.status === 'error'
                            ? 'Error'
                            : source.status === 'disabled'
                              ? 'Disabled'
                              : 'Active'}
                      </span>
                    </div>
                    <div className="type-caption" style={{ color: 'var(--text-tertiary)' }}>
                      {source.normalized_url}
                    </div>
                    {(source.last_error ?? source.feed?.last_error) && (
                      <div className="type-caption mt-2" style={{ color: 'var(--error)' }}>
                        Last fetch error: {source.last_error ?? source.feed?.last_error}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="surface-panel">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="page-eyebrow">Pro custom RSS</div>
            <div className="type-title-lg mt-1" style={{ color: 'var(--text-primary)' }}>
              Bring your own sources
            </div>
            <p
              className="type-caption mt-1"
              style={{ color: 'var(--text-tertiary)', maxWidth: '36rem' }}
            >
              Add private RSS feeds alongside the curated starter pack.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="metric-chip type-caption">
              <strong>
                {profileSyncing
                  ? 'Account sync'
                  : isPro
                    ? `${profile?.custom_feed_count ?? customSourceCount}/${customLimit}`
                    : 'Locked'}
              </strong>
              {profileSyncing ? 'temporarily unavailable' : isPro ? 'active' : 'Pro feature'}
            </span>
            {isPro && !CUSTOM_RSS_CREATION_ENABLED && (
              <span className="metric-chip type-caption">
                <strong>Preview</strong>
                creation not yet enabled
              </span>
            )}
          </div>
        </div>

        {customRssNotice && (
          <div
            className="mb-3 rounded-md px-3 py-2 type-caption"
            style={{
              background: 'var(--bg-secondary)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border-primary)',
            }}
          >
            {customRssNotice}
          </div>
        )}

        {profileSyncing ? (
          <div
            className="rounded-xl px-4 py-4"
            style={{
              background: 'rgba(255, 255, 255, 0.46)',
              border: '1px dashed var(--border-secondary)',
            }}
          >
            <div className="type-title" style={{ color: 'var(--text-primary)' }}>
              Account plan syncing
            </div>
            <p className="type-body" style={{ color: 'var(--text-secondary)', marginBottom: 0 }}>
              Custom RSS controls are temporarily unavailable while your account status reloads. The
              curated starter pack continues to work normally.
            </p>
          </div>
        ) : isPro ? (
          CUSTOM_RSS_CREATION_ENABLED ? (
            <>
              <div className="grid gap-3 md:grid-cols-[1.2fr,0.9fr,0.7fr,auto]">
                <input
                  value={customUrl}
                  onChange={(event) => setCustomUrl(event.target.value)}
                  onKeyDown={(event) => event.key === 'Enter' && void addCustomFeed()}
                  placeholder="https://example.com/rss.xml"
                  className="notion-input"
                  disabled={!CUSTOM_RSS_CREATION_ENABLED}
                />
                <input
                  value={customName}
                  onChange={(event) => setCustomName(event.target.value)}
                  placeholder="Optional display name"
                  className="notion-input"
                  disabled={!CUSTOM_RSS_CREATION_ENABLED}
                />
                <select
                  value={customPlacement}
                  onChange={(event) =>
                    setCustomPlacement(
                      event.target.value as (typeof CUSTOM_PLACEMENT_SECTIONS)[number]
                    )
                  }
                  className="notion-input"
                  disabled={!CUSTOM_RSS_CREATION_ENABLED}
                >
                  {CUSTOM_PLACEMENT_SECTIONS.map((section) => (
                    <option key={section} value={section}>
                      {formatSectionLabel(section)}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => void addCustomFeed()}
                  className="notion-btn notion-btn-primary type-label"
                  style={{ padding: '8px 16px' }}
                  disabled={creatingCustomFeed || !CUSTOM_RSS_CREATION_ENABLED}
                >
                  {creatingCustomFeed ? 'Adding...' : 'Add feed'}
                </button>
              </div>
              <div className="type-caption mt-3" style={{ color: 'var(--text-tertiary)' }}>
                Choose where the source should appear. Audio feeds are detected during validation
                and routed to Podcasts automatically. Substack-written feeds are placed under
                Letters.
              </div>
            </>
          ) : (
            <div
              className="rounded-xl px-4 py-4"
              style={{
                background: 'rgba(255, 255, 255, 0.46)',
                border: '1px dashed var(--border-secondary)',
              }}
            >
              <div className="type-title" style={{ color: 'var(--text-primary)' }}>
                Custom RSS preview
              </div>
              <p className="type-body" style={{ color: 'var(--text-secondary)', marginBottom: 0 }}>
                Creation is not turned on yet. When it is, you will paste a feed URL, optionally
                rename it, and place it into News, Research, or Newsletters.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="pill-meta type-caption">Paste RSS / Atom URL</span>
                <span className="pill-meta type-caption">Optional display name</span>
                <span className="pill-meta type-caption">Choose section placement</span>
                <span className="pill-meta type-caption">Substack writing routes to Letters</span>
              </div>
            </div>
          )
        ) : (
          <div
            className="rounded-xl px-4 py-4"
            style={{
              background: 'rgba(255, 255, 255, 0.46)',
              border: '1px dashed var(--border-secondary)',
            }}
          >
            <div className="type-title" style={{ color: 'var(--text-primary)' }}>
              Pro unlocks custom RSS
            </div>
            <p className="type-body" style={{ color: 'var(--text-secondary)', marginBottom: 0 }}>
              The curated starter list stays free. Pro accounts can add niche feeds and newsletters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
